import React, { useEffect, useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@mui/material";
import { ExpandMore, ApartmentOutlined as ApartmentOutlinedIcon } from "@mui/icons-material";
import PlaceCard from "./PlaceCard";
import CommentsView from "./CommentsView";
import NotesSection from "@/components/contributionDetails/NotesSection";
import { Place, ApiBuilding, ApiEntryPoint, ApiPlaceNotes, AccessibilitySection, DetailedNote } from "@/types/common";
import { getEntryPointIcon } from "@/utils/buildingIcons";

const tabs = [
  { label: "Notes", mr: "6%" },
  { label: "Comments", mr: 0 },
];

interface PlaceDetailsProps {
  place: Place;
  sheetPosition?: number;
}

const styles = {
  container: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#fff",
    pt: "3%",
    position: "relative",
  },
  tabsContainer: {
    borderColor: "divider",
    mb: "4%",
    px: "4%",
    bgcolor: "#fff",
  },
  tabs: {
    minHeight: "unset",
    "& .MuiTabs-indicator": { backgroundColor: "#8b5cf6" },
  },
  tab: {
    textTransform: "none",
    fontSize: { xs: "3.88vw", sm: 16 },
    minWidth: "unset",
    minHeight: "unset",
    p: "2%",
    "&.Mui-selected": { color: "#9443DF" },
  },
  contentContainer: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    px: "4%",
  },
};

const PlaceDetails: React.FC<PlaceDetailsProps> = ({ place, sheetPosition = 1 }) => {
  const [activeTab, setActiveTab] = useState(0);

  const isHalfScreen = sheetPosition < 0.9;
  const contentPadding = isHalfScreen ? "65vw" : "0";

  const buildings: ApiBuilding[] = (place?.contributions?.[0]?.buildings || [])
    .slice()
    .sort((a, b) => a.order_index - b.order_index)
    .map(building => {
      const validEntryPoints = (building.entryPoints || []).filter(ep => {
        const sectionNotes = (ep.placeNotes || []).map((pn: ApiPlaceNotes) => ({
          default_notes: (pn.default_notes || []).filter((n) => n.is_correct === true),
          user_notes: null,
        }));
        return sectionNotes.some((n) => (n.default_notes?.length ?? 0) > 0 || n.user_notes);
      });
      return {
        ...building,
        entryPoints: validEntryPoints
      };
    })
    .filter(building => building.entryPoints.length > 0);

  useEffect(() => {
    localStorage.removeItem("from_explore_to_contribute");
  }, []);

  return (
    <>
      <Box sx={styles.container}>
        <PlaceCard place={place} enableMediaPreview={true} />

        <Box sx={styles.tabsContainer}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={styles.tabs}
          >
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                label={tab.label}
                sx={{
                  ...styles.tab,
                  fontWeight: activeTab === index ? 700 : 400,
                  color: activeTab === index ? "#9443DF" : "#313131",
                  mr: tab.mr,
                }}
              />
            ))}
          </Tabs>
        </Box>

        <Box sx={{ ...styles.contentContainer, paddingBottom: contentPadding }}>
          {activeTab === 0 ? (
            buildings.length === 0 ? (
              <Box sx={{ textAlign: "center", py: "8%" }}>
                <Typography sx={{ color: "#999", fontSize: { xs: "3vw", sm: 13 } }}>
                  No accessibility notes available.
                </Typography>
              </Box>
            ) : (
              buildings.map((building: ApiBuilding) => (
                <Accordion
                  key={building.building_id}
                  elevation={0}
                  defaultExpanded
                  sx={{
                    mb: "8px",
                    border: "1px solid #E8D5FF",
                    borderRadius: "12px !important",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <Box
                        sx={{
                          width: { xs: "10vw", sm: 42 },
                          height: { xs: "10vw", sm: 42 },
                          borderRadius: "12px",
                          backgroundColor: "#F0DEFF",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <ApartmentOutlinedIcon sx={{ color: "#6f2fa8", fontSize: { xs: "5vw", sm: 22 } }} />
                      </Box>
                      <Typography sx={{ fontWeight: 600, fontSize: { xs: "3.88vw", sm: 15 }, color: "#0F172A" }}>
                        {building.building_name}
                      </Typography>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails sx={{ p: "12px" }}>
                    {[...(building.entryPoints || [])]
                      .sort((a, b) => (a.buildingSection?.section_id ?? 0) - (b.buildingSection?.section_id ?? 0))
                      .map((ep: ApiEntryPoint) => {
                        const sectionNotes: AccessibilitySection[] = (ep.placeNotes || []).map((pn: ApiPlaceNotes) => ({
                          placeSection: { section_name: ep.entry_name },
                          default_notes: (pn.default_notes || []).filter((n) => n.is_correct === true) as unknown as DetailedNote[],
                          user_notes: null as unknown as { text: string },
                        }));

                        return (
                          <Accordion
                            key={ep.entry_point_id}
                            elevation={0}
                            defaultExpanded
                            sx={{
                              border: "1px solid #F0E6FF",
                              borderRadius: "12px !important",
                              mb: "8px",
                              "&:before": { display: "none" },
                            }}
                          >
                            <AccordionSummary expandIcon={<ExpandMore sx={{ color: "#8D28E6" }} />}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <Box
                                  sx={{
                                    width: { xs: "8vw", sm: 34 },
                                    height: { xs: "8vw", sm: 34 },
                                    borderRadius: "8px",
                                    backgroundColor: "#F0DEFF",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                  }}
                                >
                                  {getEntryPointIcon(ep)}
                                </Box>
                                <Typography sx={{ fontWeight: 600, fontSize: { xs: "3.5vw", sm: 14 }, color: "#0F172A" }}>
                                  {ep.entry_name}
                                </Typography>
                              </Box>
                            </AccordionSummary>

                            <AccordionDetails sx={{ p: "12px" }}>
                              <NotesSection data={sectionNotes} optionTick={true} />
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}
                  </AccordionDetails>
                </Accordion>
              ))
            )
          ) : (
            <CommentsView place={place as Place} />
          )}
        </Box>
      </Box>
    </>
  );
};

export default PlaceDetails;
